import { USER_ROLES, type BuiltInRole, type UserRole } from '../types/enums.js';
import { USER_ROLE_LABELS } from '../labels/da.js';
import { REPORTS, REPORT_KEYS, type ReportKey } from '../reports/report.js';

/**
 * Hvem der maa hvad.
 *
 * **Rettighederne er rollernes, og de ligger i databasen** - `codepass_roles`,
 * `codepass_role_permissions` og `codepass_role_reports`, se docs/roller-opsaetning.md. En admin
 * retter dem paa /admin/roller. Det her er kataloget: hvilke rettigheder der findes, hvad de
 * hedder, hvad de kraever, og hvad en rolle har, foer nogen har rettet i den.
 *
 * Rollerne fra flest til faerrest rettigheder, som de staar fra start:
 *
 *   admin      Admin     Alt. Laast - se `LOCKED_ROLES`.
 *   finance    Finance   Hele faktureringen og alle finansrapporter. Controllerens rettigheder.
 *   sales      Saelger   Kunder og kontrakter med tal, og fakturahistorikken.
 *   developer  Udvikler  Timer og egne alarmer. Ingen kontraktsummer, kunder eller fakturaer.
 *
 * API'et haandhaever rettighederne - se `requirePermission` i apps/api/src/plugins/auth.ts og
 * `ownedPathPermission` i demo-hooken. Menuen og RoleGuard i web bruger de samme, men kun for at
 * skjule det, serveren alligevel ville afvise.
 */

export const PERMISSION_GROUPS = [
  'overview',
  'contracts',
  'hours',
  'invoicing',
  'partner_commission',
  'alerts',
  'customers',
  'reports',
  'budget',
  'administration',
] as const;
export type PermissionGroup = (typeof PERMISSION_GROUPS)[number];

export const PERMISSION_GROUP_LABELS: Record<PermissionGroup, string> = {
  overview: 'Overblik',
  contracts: 'Kontrakter',
  hours: 'Timer',
  invoicing: 'Fakturering',
  partner_commission: 'Provision',
  alerts: 'Alarmer',
  customers: 'Kunder',
  reports: 'Rapporter',
  budget: 'Budget',
  administration: 'Administration',
};

/** Noeglerne, i den raekkefoelge /admin/roller viser dem. Engelske, som resten af koden. */
export const PERMISSION_KEYS = [
  'dashboard.view',
  'contracts.view',
  'service_agreements.view',
  'hours.view',
  'invoices.drafts.view',
  'invoices.create',
  'invoices.history.view',
  'invoicing.settings',
  'subscriptions.view',
  'subscriptions.manage',
  'partner_commission.view',
  'partner_commission.email_settings',
  'partner_commission.approve',
  'partner_commission.export',
  'alerts.view_own',
  'alerts.archive_own',
  'alerts.view_all',
  'alerts.handle_all',
  'alerts.archive_all',
  'customers.view',
  'customers.without_agreement',
  'reports.export',
  'budget.view',
  'budget.manage',
  /* Administration - i samme raekkefoelge som kortene paa /admin. */
  'users.view',
  'users.manage',
  'roles.view',
  'roles.manage',
  'roles.create',
  'integrations.view',
  'integrations.reveal',
  'integrations.manage',
  'rules.view',
  'rules.manage',
  'tv_dashboard.view',
  'tv_dashboard.manage',
  'audit.view',
  'audit.view_all',
  'audit.retention',
] as const;
export type Permission = (typeof PERMISSION_KEYS)[number];

export interface PermissionDefinition {
  group: PermissionGroup;
  /** Dansk. Det, der staar ud for afkrydsningen. */
  label: string;
  /** Dansk. Hvad rettigheden aabner for. */
  description: string;
  /**
   * Rettigheder, den ikke giver mening uden - 'Lav fakturaudkast' uden 'Se fakturaudkast' ville
   * vaere en knap paa en side, man ikke kan aabne. Haandhaeves af API'et ved gem; siden saetter dem
   * selv, naar man krydser af.
   */
  requires: readonly Permission[];
}

export const PERMISSIONS: Record<Permission, PermissionDefinition> = {
  'dashboard.view': {
    group: 'overview',
    label: 'Se dashboard',
    description: 'Forsiden: faktureret i år, WIP, DSO, budget, topkunder og alles seneste alarmer.',
    requires: [],
  },
  'contracts.view': {
    group: 'contracts',
    label: 'Se kontrakter',
    description:
      'Projektaftalerne på /kontrakter: kontraktsum, timepris, timer, milepæle, tillæg og partneraftalen.',
    requires: [],
  },
  'service_agreements.view': {
    group: 'contracts',
    label: 'Se serviceaftaler',
    description:
      'Fanen Serviceaftaler på /kontrakter: timepris, timer pr. opgave og timer pr. måned.',
    requires: [],
  },
  'hours.view': {
    group: 'hours',
    label: 'Se timer',
    description: 'OnePro-opgaverne på /timer.',
    requires: [],
  },
  'invoices.drafts.view': {
    group: 'invoicing',
    label: 'Se fakturaudkast',
    description: 'Kladderne i Business Central på /fakturering.',
    requires: [],
  },
  'invoices.create': {
    group: 'invoicing',
    label: 'Lav fakturaudkast',
    description: 'Fakturér sprint på /fakturering/ny og opret kladden i Business Central.',
    requires: ['invoices.drafts.view'],
  },
  'invoices.history.view': {
    group: 'invoicing',
    label: 'Se fakturahistorik',
    description: 'De sendte fakturaer på /fakturering/historik med PDF og bilag.',
    requires: [],
  },
  'invoicing.settings': {
    group: 'invoicing',
    label: 'Ændre faktureringsopsætning',
    description:
      'Buffer på en kontrakt, og kundens faktureringsplan og fakturaregler. Planen laver selv kladder i Business Central.',
    /* The invoice schedule creates drafts every morning - see demo/auto-invoicing.ts. */
    requires: ['invoices.create'],
  },
  'subscriptions.view': {
    group: 'invoicing',
    label: 'Se abonnementsoversigt',
    description: 'Applikationsdrift og apps på /fakturering/abonnementer.',
    requires: [],
  },
  'subscriptions.manage': {
    group: 'invoicing',
    label: 'Opret og afslut abonnementer',
    description: 'Opret og afslut abonnementer.',
    requires: ['subscriptions.view'],
  },
  'partner_commission.view': {
    group: 'partner_commission',
    label: 'Se provision',
    description: 'Partnerprovisionen på /fakturering/provision og de godkendte opgørelser.',
    requires: [],
  },
  'partner_commission.email_settings': {
    group: 'partner_commission',
    label: 'Ændre mailopsætning til provision',
    description: 'Kopi, emne og tekst på mailen til partneren.',
    requires: ['partner_commission.view'],
  },
  'partner_commission.approve': {
    group: 'partner_commission',
    label: 'Godkend provision',
    description:
      'Godkend og send en opgørelse til partneren, og Send igen. En godkendelse udbetaler provision.',
    requires: ['partner_commission.view'],
  },
  'partner_commission.export': {
    group: 'partner_commission',
    label: 'Download provisionsexcel',
    description: 'Excel-filen til en opgørelse - både udkastet og de godkendte.',
    requires: ['partner_commission.view'],
  },
  'alerts.view_own': {
    group: 'alerts',
    label: 'Se mine alarmer',
    description:
      'De åbne alarmer, der er sendt til en selv, og klokken i toppen - og at tage, udsætte, løse og afvise dem.',
    requires: [],
  },
  'alerts.archive_own': {
    group: 'alerts',
    label: 'Se mine tidligere alarmer',
    description: 'Arkivet med ens egne løste og afviste alarmer.',
    requires: ['alerts.view_own'],
  },
  'alerts.view_all': {
    group: 'alerts',
    label: 'Se alle alarmer',
    description: 'Alles åbne alarmer, til at læse. Ens egne kan behandles som før.',
    requires: ['alerts.view_own'],
  },
  'alerts.handle_all': {
    group: 'alerts',
    label: 'Behandl alle alarmer',
    description: 'Tag, udsæt, løs og afvis andres alarmer - ikke kun ens egne.',
    requires: ['alerts.view_all'],
  },
  'alerts.archive_all': {
    group: 'alerts',
    label: 'Se alle tidligere alarmer',
    description: 'Arkivet med alles løste og afviste alarmer.',
    requires: ['alerts.view_all', 'alerts.archive_own'],
  },
  'customers.view': {
    group: 'customers',
    label: 'Se alle kunder',
    description: 'Kunderne fra Business Central med saldo, forfaldent, status og statistik.',
    requires: [],
  },
  'customers.without_agreement': {
    group: 'customers',
    label: 'Se alle kunder uden serviceaftale',
    description: 'Knappen "Uden serviceaftale" på /kunder.',
    requires: ['customers.view'],
  },
  'reports.export': {
    group: 'reports',
    label: 'Eksportér rapporter',
    description: 'Hent de rapporter, rollen kan se, som PDF eller CSV.',
    requires: [],
  },
  'budget.view': {
    group: 'budget',
    label: 'Se budget',
    description:
      'Budgettet på /budget pr. konto og måned, og hvad TV-skærmene måler mod. Hent det som Excel.',
    requires: [],
  },
  'budget.manage': {
    group: 'budget',
    label: 'Ændr budget',
    description:
      'Sæt budgettet på en konto for en måned eller hele året, og upload det som Excel. Flytter målene på TV-skærmene.',
    requires: ['budget.view'],
  },
  'users.view': {
    group: 'administration',
    label: 'Se brugere',
    description: 'Brugerlisten på /admin/brugere med rolle, status og sidste login.',
    requires: [],
  },
  'users.manage': {
    group: 'administration',
    label: 'Ændr brugere',
    description:
      'Skift en brugers rolle og status. Kun en admin kan give, tage eller røre admin-rollen.',
    requires: ['users.view'],
  },
  'roles.view': {
    group: 'administration',
    label: 'Se roller og rettigheder',
    description: 'Hvad hver rolle må, på /admin/roller.',
    requires: [],
  },
  'roles.manage': {
    group: 'administration',
    label: 'Ændr roller og rettigheder',
    description: 'Ret hvad hver rolle må - også ens egen rolle. Admin kan ikke rettes.',
    requires: ['roles.view'],
  },
  'roles.create': {
    group: 'administration',
    label: 'Opret og slet roller',
    description:
      'Opret nye roller på /admin/roller, omdøb roller, og slet dem, der ikke har brugere. Det gælder også de fire, rollerne startede med.',
    /* A new role starts with nothing - without 'Ændr roller' it could never be given anything. */
    requires: ['roles.manage'],
  },
  'integrations.view': {
    group: 'administration',
    label: 'Se integrationer',
    description: 'Kildesystemernes status og opsætning på /admin/integrationer - uden nøglerne.',
    requires: [],
  },
  'integrations.reveal': {
    group: 'administration',
    label: 'Se integrationsnøgler',
    description:
      'Vis en gemt nøgle i klartekst på /admin/integrationer. Hver visning skrives i audit-loggen.',
    requires: ['integrations.view'],
  },
  'integrations.manage': {
    group: 'administration',
    label: 'Ændr integrationer',
    description: 'Ret og slet nøgler, test Teams-kanalen, og start en synk.',
    /* Editing a key is seeing it: the field shows the stored value you type over. */
    requires: ['integrations.view', 'integrations.reveal'],
  },
  'rules.view': {
    group: 'administration',
    label: 'Se regler',
    description: 'Detekteringsreglerne på /admin/regler og notifikationsmodtagerne, til at læse.',
    requires: [],
  },
  'rules.manage': {
    group: 'administration',
    label: 'Ændr regler',
    description:
      'Slå regler til og fra, ret tekst, tærskler og frekvens, send en testbesked og "Kør nu". Ikke Auditoprydning - den er "Ændr opbevaring af audit-log".',
    requires: ['rules.view'],
  },
  'tv_dashboard.view': {
    group: 'administration',
    label: 'Se TV-dashboard',
    description: 'Opsætningen af storskærmen på /admin/tv-dashboard: visning og kalender.',
    requires: [],
  },
  'tv_dashboard.manage': {
    group: 'administration',
    label: 'Ændr TV-dashboard',
    description: 'Ret visning, faktureringsgrad og dagene i kalenderen.',
    requires: ['tv_dashboard.view'],
  },
  'audit.view': {
    group: 'administration',
    label: 'Se egen audit-log',
    description: 'Audit-loggen med ens egne handlinger.',
    requires: [],
  },
  'audit.view_all': {
    group: 'administration',
    label: 'Se alles audit-log',
    description: 'Audit-loggen med alles handlinger og filteret på bruger.',
    requires: ['audit.view'],
  },
  'audit.retention': {
    group: 'administration',
    label: 'Ændr opbevaring af audit-log',
    description:
      'Reglen Auditoprydning på /admin/regler: fristerne, at slå den til og fra, og at den kommer med, når "Kør nu" kører reglerne. Den sletter poster i audit-loggen - også de andre OneCode-apps’.',
    /* Whoever decides how long the log is kept must be able to read it, and the rule it is on. */
    requires: ['rules.view', 'audit.view_all'],
  },
};

/**
 * Rettighederne bag Administration. Har en rolle blot én af dem, staar menupunktet der, og /admin
 * viser de kort, rollen kan aabne - se `ADMIN_SECTIONS` i web.
 */
export const ADMIN_PERMISSIONS: readonly Permission[] = PERMISSION_KEYS.filter(
  (key) => PERMISSIONS[key].group === 'administration',
);

/**
 * Roller, der ikke kan rettes. **Admin har altid alt** - ogsaa en rettighed, der kommer til
 * senere - og det haandhaeves her, ikke af raekker i databasen: saa kan ingen fjerne 'Ændr roller
 * og rettigheder' fra admin og laase alle ude af /admin/roller.
 *
 * Det er ogsaa kun en bruger med en laast rolle, der kan give, tage eller roere en bruger med
 * den - se `canManageUser`. Ellers kunne 'Ændr brugere' goere sig selv til admin ad omvejen.
 *
 * The lock follows the key, not the name: renamed, Admin still has everything. Deleted - which
 * takes a role nobody has - there is no locked role left, and every role has what it was given.
 */
export const LOCKED_ROLES: readonly BuiltInRole[] = ['admin'];

export function isLockedRole(role: UserRole): boolean {
  return (LOCKED_ROLES as readonly string[]).includes(role);
}

export function isBuiltInRole(role: UserRole): role is BuiltInRole {
  return (USER_ROLES as readonly string[]).includes(role);
}

/**
 * A role as /admin/roller, the user list and the API know it - one of the four built-in, or one
 * an admin created. A list of them is in precedence order: the built-in first, as `USER_ROLES`,
 * then the created ones in the order they were made.
 */
export interface RoleDefinition {
  role: UserRole;
  /** Dansk. Det, der staar ved brugeren og over kolonnen paa /admin/roller. */
  label: string;
  /**
   * One of `USER_ROLES` - the four the roles started with. They rank first and have default rights
   * before the tables exist; otherwise they are renamed and deleted like any other role.
   */
  built_in: boolean;
  /** Always has everything and cannot be edited - see `LOCKED_ROLES`. */
  locked: boolean;
}

/** The four built-in roles - the whole catalogue until an admin creates one. */
export const BUILT_IN_ROLE_DEFINITIONS: readonly RoleDefinition[] = USER_ROLES.map((role) => ({
  role,
  label: USER_ROLE_LABELS[role],
  built_in: true,
  locked: isLockedRole(role),
}));

/** A role's name. The key itself only when the catalogue does not know it - better than nothing. */
export function roleLabel(
  role: UserRole | null | undefined,
  catalogue: readonly RoleDefinition[] = BUILT_IN_ROLE_DEFINITIONS,
): string {
  if (!role) return 'Ingen rolle';
  return (
    catalogue.find((entry) => entry.role === role)?.label ??
    (isBuiltInRole(role) ? USER_ROLE_LABELS[role] : role)
  );
}

/** A created role's key: lower-case letters, digits and underscores, starting with a letter. */
export const ROLE_KEY_PATTERN = /^[a-z][a-z0-9_]{1,39}$/;

/**
 * Keys a created role can never have: the built-in and their names before 08-10-2026. A row with
 * an old name is read as the role that replaced it - see `parseUserRole` - so a role called
 * 'controller' would quietly be Finance.
 */
export const RESERVED_ROLE_KEYS: readonly string[] = [
  ...USER_ROLES,
  'controller',
  'project_manager',
  'consultant',
];

/**
 * The key for a new role, made from its Danish name: "Projektleder" is `projektleder`, "Økonomi &
 * HR" is `oekonomi_hr`. English like the rest of the keys would be nicer, but a key nobody has to
 * think up is the one that gets used. A suffix makes it unique - `projektleder_2`.
 */
export function roleKeyFromLabel(label: string, taken: Iterable<string>): string {
  const base =
    label
      .trim()
      .toLowerCase()
      .replaceAll('æ', 'ae')
      .replaceAll('ø', 'oe')
      .replaceAll('å', 'aa')
      .normalize('NFD')
      /* The accents NFD split off - é is e and an accent. */
      .replaceAll(/\p{M}/gu, '')
      .replaceAll(/[^a-z0-9]+/g, '_')
      .replaceAll(/^_+|_+$/g, '')
      .slice(0, 34)
      .replace(/_+$/, '') || 'rolle';
  const start = /^[a-z]/.test(base) ? base : `rolle_${base}`.slice(0, 34);

  const used = new Set([...RESERVED_ROLE_KEYS, ...taken]);
  if (!used.has(start) && ROLE_KEY_PATTERN.test(start)) return start;
  for (let suffix = 2; ; suffix += 1) {
    const candidate = `${start}_${suffix}`;
    if (!used.has(candidate)) return candidate;
  }
}

/**
 * Om en bruger med `actorRole` maa aendre en bruger, der har `targetRole`, eventuelt til
 * `nextRole`. 'Ændr brugere' raekker ikke til en admin - hverken at goere nogen til admin, at
 * tage rollen fra en, eller at deaktivere en. Kun en admin kan det.
 */
export function canManageUser(
  actorRole: UserRole,
  targetRole: UserRole | null,
  nextRole: UserRole | null = null,
): boolean {
  if (isLockedRole(actorRole)) return true;
  if (targetRole && isLockedRole(targetRole)) return false;
  return !(nextRole && isLockedRole(nextRole));
}

/** Det, en rolle maa: rettighederne og de rapporter, den kan se. */
export interface RoleAccess {
  permissions: readonly Permission[];
  reports: readonly ReportKey[];
}

/** What each role may, by key - the built-in and the created ones. A missing role may nothing. */
export type RoleAccessMap = Readonly<Record<UserRole, RoleAccess>>;

/** Alt - det, en laast rolle har. */
export const FULL_ACCESS: RoleAccess = { permissions: PERMISSION_KEYS, reports: REPORT_KEYS };

/**
 * Det, rollerne havde, da rettighederne var en liste i koden. Seedet i databasen af
 * docs/roller-opsaetning.md, og det, en rolle har, saa laenge tabellerne ikke findes, eller
 * rollen ikke har en raekke i `codepass_roles`.
 */
const DEFAULT_PERMISSIONS: Record<BuiltInRole, readonly Permission[]> = {
  admin: PERMISSION_KEYS,
  finance: [
    'dashboard.view',
    'contracts.view',
    'service_agreements.view',
    'hours.view',
    'invoices.drafts.view',
    'invoices.create',
    'invoices.history.view',
    'invoicing.settings',
    'subscriptions.view',
    'subscriptions.manage',
    'alerts.view_own',
    'alerts.archive_own',
    'alerts.view_all',
    'alerts.handle_all',
    'alerts.archive_all',
    'customers.view',
    'customers.without_agreement',
    'reports.export',
    'audit.view',
    'rules.view',
  ],
  sales: [
    'contracts.view',
    'service_agreements.view',
    'hours.view',
    'invoices.history.view',
    'alerts.view_own',
    'alerts.archive_own',
    'customers.view',
    'customers.without_agreement',
    'reports.export',
    'audit.view',
  ],
  developer: ['hours.view', 'alerts.view_own', 'alerts.archive_own'],
};

function defaultAccess(role: BuiltInRole): RoleAccess {
  return {
    permissions: sortPermissions(DEFAULT_PERMISSIONS[role]),
    reports: sortReports(
      REPORTS.filter((report) => report.defaultRoles.includes(role)).map((report) => report.key),
    ),
  };
}

/** The built-in roles only: a created role starts with nothing. */
export const DEFAULT_ROLE_ACCESS: Readonly<Record<BuiltInRole, RoleAccess>> = {
  admin: defaultAccess('admin'),
  finance: defaultAccess('finance'),
  sales: defaultAccess('sales'),
  developer: defaultAccess('developer'),
};

export function isPermission(value: string): value is Permission {
  return (PERMISSION_KEYS as readonly string[]).includes(value);
}

export function isReportKey(value: string): value is ReportKey {
  return (REPORT_KEYS as readonly string[]).includes(value);
}

/** Kataloget raekkefoelge, uden dubletter. */
export function sortPermissions(permissions: Iterable<Permission>): Permission[] {
  const present = new Set(permissions);
  return PERMISSION_KEYS.filter((permission) => present.has(permission));
}

export function sortReports(reports: Iterable<ReportKey>): ReportKey[] {
  const present = new Set(reports);
  return REPORT_KEYS.filter((key) => present.has(key));
}

/** Rettighederne plus alt, de kraever - hele vejen ned. Det, siden goer, naar man krydser af. */
export function withRequirements(permissions: Iterable<Permission>): Permission[] {
  const result = new Set<Permission>();
  const add = (permission: Permission): void => {
    if (result.has(permission)) return;
    result.add(permission);
    for (const required of PERMISSIONS[permission].requires) add(required);
  };
  for (const permission of permissions) add(permission);
  return sortPermissions(result);
}

/** Rettighederne uden `removed` og alt, der kraever den. Det, siden goer, naar man fjerner et kryds. */
export function withoutDependents(
  permissions: Iterable<Permission>,
  removed: Permission,
): Permission[] {
  const result = new Set(permissions);
  const drop = (permission: Permission): void => {
    if (!result.delete(permission)) return;
    for (const candidate of PERMISSION_KEYS) {
      if (PERMISSIONS[candidate].requires.includes(permission)) drop(candidate);
    }
  };
  drop(removed);
  return sortPermissions(result);
}

/** Hver rettighed, der mangler noget, den kraever. Tom, naar saettet haenger sammen. */
export function missingRequirements(
  permissions: readonly Permission[],
): Array<{ permission: Permission; requires: Permission }> {
  const present = new Set(permissions);
  return sortPermissions(present).flatMap((permission) =>
    PERMISSIONS[permission].requires
      .filter((required) => !present.has(required))
      .map((required) => ({ permission, requires: required })),
  );
}

/**
 * The permissions without every one whose requirements are not met - all the way down. What a
 * stored role really has: a requirement added to the catalogue later must hold for rows saved
 * before it, and access fails closed, so the dependent goes rather than the requirement being
 * granted.
 */
export function withoutUnmetRequirements(permissions: Iterable<Permission>): Permission[] {
  let result = sortPermissions(permissions);
  for (;;) {
    const unmet = new Set(missingRequirements(result).map((missing) => missing.permission));
    if (unmet.size === 0) return result;
    result = result.filter((permission) => !unmet.has(permission));
  }
}

/**
 * Det, en bruger med disse roller maa: **foreningsmaengden** af rollernes rettigheder. En ekstra
 * rolle maa aldrig kunne fjerne adgang, brugeren allerede havde. En laast rolle giver alt.
 */
export function accessForRoles(roles: readonly UserRole[], map: RoleAccessMap): RoleAccess {
  if (roles.some(isLockedRole)) return FULL_ACCESS;

  const permissions = new Set<Permission>();
  const reports = new Set<ReportKey>();
  for (const role of roles) {
    /* A role the map does not know - deleted since the user was given it - gives nothing. */
    const access = map[role];
    if (!access) continue;
    for (const permission of access.permissions) permissions.add(permission);
    for (const report of access.reports) reports.add(report);
  }
  return { permissions: sortPermissions(permissions), reports: sortReports(reports) };
}

/** Den, der spoerges om: en bruger med sine rettigheder - `AuthenticatedUser` passer. */
export interface AccessSubject {
  permissions: readonly Permission[];
}

export function can(subject: AccessSubject | null | undefined, permission: Permission): boolean {
  return Boolean(subject?.permissions.includes(permission));
}

/** Mindst én af dem - fx en side, der aabnes af baade fakturaudkast og historik. */
export function canAny(
  subject: AccessSubject | null | undefined,
  permissions: readonly Permission[],
): boolean {
  return permissions.some((permission) => can(subject, permission));
}

export function canSeeReport(
  subject: { reports: readonly ReportKey[] } | null | undefined,
  key: ReportKey,
): boolean {
  return Boolean(subject?.reports.includes(key));
}

/**
 * Whether the user can open a contract on /kontrakter/:id - the same split as the API's
 * `visibleContractKinds`: a service agreement is 'Se serviceaftaler', any other 'Se kontrakter'.
 *
 * A full contract carries `service_agreement` (null on everything but a service agreement), so its
 * kind is known. A bare reference - on an invoice, a subscription, a commission line - does not,
 * so it takes both: a link must not lead to a page the API then answers with "findes ikke".
 */
export function canOpenContract(
  subject: AccessSubject | null | undefined,
  contract: object | null | undefined,
): boolean {
  if (contract && 'service_agreement' in contract) {
    const service = Boolean((contract as { service_agreement?: unknown }).service_agreement);
    return can(subject, service ? 'service_agreements.view' : 'contracts.view');
  }
  return can(subject, 'contracts.view') && can(subject, 'service_agreements.view');
}

/**
 * Whether the user can open an invoice on /fakturering/:id - the same split as the API's
 * `invoiceVisibility`: a draft is 'Se fakturaudkast', anything else 'Se fakturahistorik'. Without
 * a status - an invoice number on a task or a commission line - it takes both.
 */
export function canOpenInvoice(
  subject: AccessSubject | null | undefined,
  status: string | null | undefined,
): boolean {
  if (status === 'draft') return can(subject, 'invoices.drafts.view');
  if (status) return can(subject, 'invoices.history.view');
  return can(subject, 'invoices.drafts.view') && can(subject, 'invoices.history.view');
}

/**
 * What it takes to change a detection rule on /admin/regler. Auditoprydning deletes audit-log
 * entries, so it has its own permission - see domain/rules/auditoprydning.ts in the API.
 */
export function rulePermission(ruleName: string): Permission {
  return ruleName === 'auditoprydning' ? 'audit.retention' : 'rules.manage';
}

/**
 * Whether the user may take, snooze, resolve or dismiss an alarm sent to `recipientId`. Your own
 * come with 'Se mine alarmer'; anyone else's need 'Behandl alle alarmer' - 'Se alle alarmer'
 * alone only reads them. An alarm without a known recipient is someone else's.
 */
export function canHandleAlarm(
  subject: (AccessSubject & { id: string }) | null | undefined,
  recipientId: string | null | undefined,
): boolean {
  if (can(subject, 'alerts.handle_all')) return true;
  return Boolean(recipientId) && recipientId === subject?.id && can(subject, 'alerts.view_own');
}

/**
 * Rollernes navne foer 08-10-2026. En raekke i `codepass_user_roles` med et af dem laeses som
 * den rolle, der afloeste det - ellers ville en bruger, ingen har roert, miste sin adgang ved
 * omdoebningen. Der skrives kun de nye navne.
 */
const LEGACY_ROLES: Readonly<Record<string, BuiltInRole>> = {
  controller: 'finance',
  project_manager: 'sales',
  consultant: 'developer',
};

/**
 * En raekke fra `codepass_user_roles` som rolle. Ukendte vaerdier er null og ikke noget, der
 * ligner: adgang skal fejle lukket, ikke aabent.
 *
 * `known` is the roles that exist, in precedence order - the built-in, plus the created ones when
 * the caller has read them from `codepass_roles`.
 */
export function parseUserRole(
  value: string | null | undefined,
  known: readonly UserRole[] = USER_ROLES,
): UserRole | null {
  const normalized = value?.trim().toLowerCase();
  if (!normalized) return null;
  if (known.includes(normalized)) return normalized;
  /* An old name reads as its successor - while that still exists. A deleted role gives nothing. */
  const successor = LEGACY_ROLES[normalized];
  return successor && known.includes(successor) ? successor : null;
}

/** Alle brugerens kendte roller, i forrangsraekkefoelge og uden dubletter. */
export function knownRoles(
  values: Iterable<string | null | undefined>,
  known: readonly UserRole[] = USER_ROLES,
): UserRole[] {
  const present = new Set<UserRole>();
  for (const value of values) {
    const role = parseUserRole(value, known);
    if (role) present.add(role);
  }
  return known.filter((role) => present.has(role));
}

/**
 * Den mest vidtgaaende af brugerens roller - den, der staar ved navnet. `known` staar i
 * forrangsraekkefoelge. Hvad brugeren maa, er alle rollernes - se `accessForRoles`.
 */
export function highestRole(
  values: Iterable<string | null | undefined>,
  known: readonly UserRole[] = USER_ROLES,
): UserRole | null {
  return knownRoles(values, known)[0] ?? null;
}

/**
 * Forsiden af Fakturering: den foerste af fanerne, brugeren kan aabne. Null uden nogen af dem -
 * saa er menupunktet vaek.
 */
export function invoicingHomePath(subject: AccessSubject | null | undefined): string | null {
  if (can(subject, 'invoices.drafts.view')) return '/fakturering';
  if (can(subject, 'invoices.history.view')) return '/fakturering/historik';
  if (can(subject, 'subscriptions.view')) return '/fakturering/abonnementer';
  if (can(subject, 'partner_commission.view')) return '/fakturering/provision';
  return null;
}

/**
 * Hvor en bruger lander efter login og paa "/": det foerste, rettighederne aabner. Profilen kan
 * alle se, saa en rolle uden en eneste rettighed lander dér og ikke paa en side, der siger nej.
 */
export function homePath(
  subject: (AccessSubject & { reports?: readonly ReportKey[] }) | null | undefined,
): string {
  if (can(subject, 'dashboard.view')) return '/dashboard';
  if (can(subject, 'contracts.view')) return '/kontrakter';
  if (can(subject, 'service_agreements.view')) return '/kontrakter?kind=service';
  if (can(subject, 'hours.view')) return '/timer';
  const invoicing = invoicingHomePath(subject);
  if (invoicing) return invoicing;
  if (can(subject, 'customers.view')) return '/kunder';
  if ((subject?.reports?.length ?? 0) > 0) return '/rapporter';
  if (can(subject, 'alerts.view_own')) return '/alarmer';
  if (can(subject, 'budget.view')) return '/budget';
  if (canAny(subject, ADMIN_PERMISSIONS)) return '/admin';
  return '/profil';
}
